import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMobile } from "../hooks/useMobile";
import Sidebar from "../components/Sidebar";
import LeadsKanbanBoard from "../components/LeadsKanbanBoard";
import BrokerMessageThread from "../components/BrokerMessageThread";
import api from "../services/api";
import "./EnquiriesPage.css";

// ─────────────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || "";
const tok = () => localStorage.getItem("token");
function authHeaders() {
  return { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" };
}

async function downloadCsv(url, filename) {
  try {
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) { alert("Export failed"); return; }
    const blob = await res.blob();
    const objUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(objUrl);
  } catch { alert("Export failed"); }
}

const URGENCY = {
  hot:  { label: "Hot",  colour: "#dc2626", bg: "#fff1f1", border: "#fca5a5", icon: "🔥" },
  warm: { label: "Warm", colour: "#d97706", bg: "#fffbeb", border: "#fcd34d", icon: "☀️" },
  cold: { label: "Cold", colour: "#2563eb", bg: "#eff6ff", border: "#93c5fd", icon: "❄️" },
};

const SOURCE_LABELS = {
  walk_in:        "Walk-in",
  phone:          "Phone",
  online:         "Online",
  referral:       "Referral",
  website_widget: "Website Widget",
  fb_remortgage_funnel: "FB — Remortgage (Ads)",
  fb_remortgage_funnel_organic: "FB — Remortgage (Organic)",
  fb_ftb_funnel: "FB — First-time buyer",
  fb_btl_funnel: "FB — Buy-to-let",
  fb_leadads: "FB — Lead Ads",
  other:          "Other",
};

const PRODUCT_TYPES = [
  { value: "", label: "— Select product —" },
  { value: "residential_purchase",   label: "Residential Purchase" },
  { value: "residential_remortgage", label: "Residential Remortgage" },
  { value: "buy_to_let_purchase",    label: "Buy to Let Purchase" },
  { value: "buy_to_let_remortgage",  label: "Buy to Let Remortgage" },
  { value: "commercial",             label: "Commercial" },
  { value: "bridging",               label: "Bridging" },
  { value: "other",                  label: "Other" },
];

const MORTGAGE_TYPES = [
  { value: "", label: "— Select type —" },
  { value: "Purchase",        label: "Purchase" },
  { value: "Remortgage",      label: "Remortgage" },
  { value: "First-time buyer",label: "First-time buyer" },
  { value: "Buy-to-let",      label: "Buy to Let" },
  { value: "Commercial",      label: "Commercial" },
  { value: "Bridging",        label: "Bridging" },
  { value: "Other",           label: "Other" },
];

// Stage metadata for each source's own status vocabulary.
const DIRECT_STAGE = {
  new:         { bg: "#f0f9ff", colour: "#0369a1", label: "New" },
  assigned:    { bg: "#eef2ff", colour: "#4f46e5", label: "Assigned" },
  in_progress: { bg: "#f0fdf4", colour: "#15803d", label: "In Progress" },
  converted:   { bg: "#d1fae5", colour: "#065f46", label: "Converted" },
  lost:        { bg: "#fef2f2", colour: "#991b1b", label: "Lost" },
};

const INTRO_STAGE = {
  submitted:         { bg: "#fffbeb", colour: "#92400e", label: "Pending review" },
  approved:          { bg: "#eef2ff", colour: "#4f46e5", label: "Approved" },
  rejected:          { bg: "#fef2f2", colour: "#991b1b", label: "Rejected" },
  converted:         { bg: "#d1fae5", colour: "#065f46", label: "Converted" },
  awaiting_consent:  { bg: "#fdf2f8", colour: "#9d174d", label: "Awaiting verification" },
  consent_declined:  { bg: "#fef2f2", colour: "#991b1b", label: "Client declined" },
  consent_expired:   { bg: "#f3f4f6", colour: "#6b7280", label: "Verification expired" },
};

const PRE_CONSENT_STAGES = ["awaiting_consent", "consent_declined", "consent_expired"];

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

function ageLabel(iso) {
  const mins = (Date.now() - new Date(iso)) / 60_000;
  if (mins < 60) return `${Math.round(mins)}m ago`;
  const hrs = mins / 60;
  if (hrs < 24) return `${Math.round(hrs)}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function ageLabelDetailed(iso) {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60_000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs < 24) return rem > 0 ? `${hrs}h ${rem}m ago` : `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// Mirrors the 24h / 48h chase cadence in jobs/consentChaser.js.
function consentChaseStatus(lead) {
  if (lead.consent_chase_2_at) {
    const daysLeft = Math.max(0, Math.ceil((new Date(lead.consent_expires_at) - Date.now()) / 86_400_000));
    return { label: `48h reminder sent · expires in ${daysLeft}d`, color: "#dc2626" };
  }
  if (lead.consent_chase_1_at) {
    return { label: "24h reminder sent to client", color: "#d97706" };
  }
  const hrs = (Date.now() - new Date(lead.consent_requested_at || lead.created_at)) / 3_600_000;
  if (hrs >= 24) return { label: "24h reminder due shortly", color: "#d97706" };
  return { label: `First reminder in ${Math.max(1, Math.round(24 - hrs))}h if no response`, color: "#94a3b8" };
}

// ─────────────────────────────────────────────────────────────────────────
// Normalisation — merges the two source shapes into one common row shape
// used for the combined list, filtering, sorting and search.
// ─────────────────────────────────────────────────────────────────────────
function normalizeIntroducer(lead) {
  const redacted = PRE_CONSENT_STAGES.includes(lead.status);
  return {
    uid: `i-${lead.id}`,
    id: lead.id,
    source: "introducer",
    stage: lead.status,
    stageMeta: INTRO_STAGE[lead.status] || { bg: "#f3f4f6", colour: "#6b7280", label: lead.status },
    client_name: redacted ? null : lead.client_name,
    client_email: redacted ? null : lead.client_email,
    client_phone: redacted ? null : lead.client_phone,
    urgency: lead.urgency,
    assigned_broker_id: lead.assigned_broker_id,
    assigned_broker_name: lead.assigned_broker_name,
    created_at: lead.created_at,
    unread_count: lead.unread_count || 0,
    introducer_name: lead.introducer_name,
    sourceLabel: "Introducer",
    raw: lead,
  };
}

function normalizeDirect(enq) {
  return {
    uid: `d-${enq.id}`,
    id: enq.id,
    source: "direct",
    stage: enq.status,
    stageMeta: DIRECT_STAGE[enq.status] || { bg: "#f3f4f6", colour: "#6b7280", label: enq.status },
    client_name: enq.client_name,
    client_email: enq.client_email,
    client_phone: enq.client_phone,
    urgency: enq.urgency,
    assigned_broker_id: enq.assigned_broker_id,
    assigned_broker_name: enq.assigned_broker_name,
    created_at: enq.created_at,
    unread_count: 0,
    introducer_name: null,
    sourceLabel: SOURCE_LABELS[enq.source] || "Direct",
    raw: enq,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Small shared badge components
// ─────────────────────────────────────────────────────────────────────────
function SourceTag({ source, rawSource }) {
  if (rawSource && rawSource.startsWith("fb_")) {
    return <span className="eq-badge" style={{ background: "#e7f0ff", color: "#0866ff", border: "1px solid #b6d0ff" }}>📘 Facebook</span>;
  }
  return source === "introducer"
    ? <span className="eq-badge" style={{ background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0" }}>🤝 Introducer</span>
    : <span className="eq-badge" style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}>📋 Direct</span>;
}

function StageBadge({ stageMeta }) {
  return <span className="eq-badge" style={{ background: stageMeta.bg, color: stageMeta.colour }}>{stageMeta.label}</span>;
}

function UrgencyBadgeC({ urgency }) {
  if (!urgency) return null;
  const u = URGENCY[urgency];
  // Introducer-portal lead submissions (SubmitLead.js) use their own vocabulary
  // ("Urgent" / "Standard" / "No rush") distinct from the broker-side hot/warm/cold
  // scale used everywhere else. Rather than silently dropping the badge for those
  // (the previous per-tab UI's behaviour), show the raw value in a neutral style
  // so it's still visible — just not colour-coded or filterable the same way.
  if (!u) return <span className="eq-badge" style={{ background: "#f1f5f9", color: "#475569" }}>{urgency}</span>;
  return <span className="eq-badge" style={{ background: u.bg, color: u.colour, border: `1px solid ${u.border}` }}>{u.icon} {u.label}</span>;
}

function AssignBadge({ item, currentUserId }) {
  if (item.assigned_broker_id && item.assigned_broker_id === currentUserId) {
    return <span className="eq-badge" style={{ background: "#eef2ff", color: "#4338ca" }}>🎯 Assigned to you</span>;
  }
  if (item.assigned_broker_name) {
    return <span className="eq-badge" style={{ background: "#f1f5f9", color: "#475569" }}>→ {item.assigned_broker_name}</span>;
  }
  return <span className="eq-badge" style={{ background: "#fffbeb", color: "#92400e", border: "1px solid #fcd34d" }}>Unassigned</span>;
}

function ViewToggle({ viewMode, setViewMode }) {
  const btn = (mode, label, icon) => (
    <button onClick={() => setViewMode(mode)}
      style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", fontSize: 13, fontWeight: 600,
        cursor: "pointer", border: "none", borderRadius: 7,
        background: viewMode === mode ? "#fff" : "transparent",
        color: viewMode === mode ? "#0e1b2c" : "#6b7280",
        boxShadow: viewMode === mode ? "0 1px 2px rgba(0,0,0,0.08)" : "none" }}>
      <span>{icon}</span>{label}
    </button>
  );
  return (
    <div style={{ display: "flex", gap: 2, background: "#f3f4f6", borderRadius: 9, padding: 3 }}>
      {btn("list", "List", "☰")}
      {btn("board", "Board", "▦")}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Introducer-lead detail pieces (ported from LeadInbox.js, unchanged logic)
// BrokerMessageThread itself now lives in components/BrokerMessageThread.jsx
// (imported above) — reused as-is by pages/DealDetails.js's new Introducer
// Conversation card so the two never drift apart.
// ─────────────────────────────────────────────────────────────────────────
const MetaLabel = ({ label, value, valueStyle }) => (
  <div>
    <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8", display: "block", marginBottom: 2 }}>{label}</span>
    <span style={{ fontSize: 13.5, color: "#1e293b", ...valueStyle }}>{value}</span>
  </div>
);

function NotesBox({ lead, onSaveNotes }) {
  const [notes, setNotes] = useState(lead.broker_notes || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = notes !== (lead.broker_notes || "");
  const save = async () => {
    setSaving(true);
    await onSaveNotes(lead.id, notes);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return (
    <div style={{ marginTop: 12 }}>
      <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8", display: "block", marginBottom: 5 }}>Your notes</span>
      <textarea
        placeholder="Add a note before accepting or rejecting…"
        value={notes} onChange={e => setNotes(e.target.value)} rows={2}
        style={{ width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", background: "#fffef5", outline: "none" }}
      />
      {(dirty || saved) && (
        <button onClick={save} disabled={saving || !dirty} className={saved ? "" : "eq-btn-secondary"}
          style={saved ? { marginTop: 6, background: "#d1fae5", color: "#065f46", border: "none", borderRadius: 6, padding: "6px 14px", fontWeight: 600, cursor: "pointer", fontSize: 12 } : { marginTop: 6 }}>
          {saving ? "Saving…" : saved ? "✓ Saved" : "Save note"}
        </button>
      )}
    </div>
  );
}

const RAG_OPTIONS = [
  { key: "no_reply",       icon: "📞", label: "Tried — no reply",  bg: "#fffbeb", border: "#fcd34d", color: "#92400e" },
  { key: "meeting_booked", icon: "✅", label: "Meeting booked",    bg: "#d1fae5", border: "#6ee7b7", color: "#065f46" },
  { key: "no_discussion",  icon: "🚫", label: "Would not discuss", bg: "#fee2e2", border: "#fca5a5", color: "#991b1b" },
];

function RagStrip({ lead, onContactStatus }) {
  const [busy, setBusy] = useState(false);
  const click = async (key) => {
    if (key === "no_discussion" &&
        !window.confirm("Close this lead? The introducer will be emailed to say contact was made but the client would not discuss their situation.")) return;
    setBusy(true);
    await onContactStatus(lead.id, key);
    setBusy(false);
  };
  return (
    <div style={{ width: "100%" }}>
      <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8", display: "block", marginBottom: 8 }}>
        Contact progress {lead.contact_attempts > 0 && `· ${lead.contact_attempts} attempt${lead.contact_attempts !== 1 ? "s" : ""}`}
      </span>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {RAG_OPTIONS.map(o => {
          const active = lead.contact_status === o.key;
          return (
            <button key={o.key} onClick={() => click(o.key)} disabled={busy}
              style={{
                background: active ? o.bg : "#f8fafc", border: `1.5px solid ${active ? o.border : "#e2e8f0"}`,
                color: active ? o.color : "#4b5563", borderRadius: 8, padding: "8px 14px",
                fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", fontSize: 13,
                display: "flex", alignItems: "center", gap: 6,
              }}>
              {o.icon} {o.label}{active && o.key === "no_reply" && lead.contact_attempts > 0 ? ` (${lead.contact_attempts})` : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const PENDING_CONTACT_OPTIONS = [
  { key: "call_attempted", icon: "📞", label: "Called — no answer", bg: "#fffbeb", border: "#fcd34d", color: "#92400e" },
  { key: "call_made",      icon: "💬", label: "Spoke to client",    bg: "#eff6ff", border: "#93c5fd", color: "#1e40af" },
];

function PendingContactStrip({ lead, onContactStatus }) {
  const [busy, setBusy] = useState(false);
  const click = async (key) => { setBusy(true); await onContactStatus(lead.id, key); setBusy(false); };
  return (
    <div style={{ width: "100%", marginBottom: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8", display: "block", marginBottom: 8 }}>
        Call status {lead.contact_attempts > 0 && `· ${lead.contact_attempts} attempt${lead.contact_attempts !== 1 ? "s" : ""}`}
      </span>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {PENDING_CONTACT_OPTIONS.map(o => {
          const active = lead.contact_status === o.key;
          return (
            <button key={o.key} onClick={() => click(o.key)} disabled={busy}
              style={{
                background: active ? o.bg : "#f8fafc", border: `1.5px solid ${active ? o.border : "#e2e8f0"}`,
                color: active ? o.color : "#4b5563", borderRadius: 8, padding: "8px 14px",
                fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", fontSize: 13,
                display: "flex", alignItems: "center", gap: 6,
              }}>
              {o.icon} {o.label}{active && o.key === "call_attempted" && lead.contact_attempts > 1 ? ` (${lead.contact_attempts}x)` : ""}
            </button>
          );
        })}
      </div>
      {lead.contact_status && (
        <p style={{ margin: "6px 0 0", fontSize: 12, color: "#64748b" }}>
          {lead.contact_status === "call_attempted" ? "📧 Introducer notified — we tried to reach the client." : "📧 Introducer notified — client contacted."}
        </p>
      )}
    </div>
  );
}

// Redacted card for the three pre-consent stages (awaiting_consent / consent_declined / consent_expired).
function RedactedIntroCard({ lead }) {
  const introducerLabel = lead.introducer_name || "An introducer";
  if (lead.status === "awaiting_consent") {
    const chase = consentChaseStatus(lead);
    return (
      <div>
        <div style={{ fontSize: 14, color: "#0e1b2c", lineHeight: 1.4 }}>
          <strong>{introducerLabel}</strong> sent a lead — <span style={{ color: "#9d174d", fontWeight: 600 }}>awaiting client consent</span>
        </div>
        <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 8 }}>
          Sent {ageLabelDetailed(lead.consent_requested_at || lead.created_at)}. Client identity is hidden until they confirm — you'll see full details the moment they respond.
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: chase.color, marginTop: 12 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: chase.color, display: "inline-block" }} />
          {chase.label}
        </div>
      </div>
    );
  }
  if (lead.status === "consent_declined") {
    return (
      <div style={{ fontSize: 14, color: "#0e1b2c", lineHeight: 1.5 }}>
        A client referred by <strong>{introducerLabel}</strong> chose not to proceed and declined the verification request. Their details were never shared and no further action is needed.
      </div>
    );
  }
  return (
    <div style={{ fontSize: 14, color: "#0e1b2c", lineHeight: 1.5 }}>
      A client referred by <strong>{introducerLabel}</strong> did not respond to the verification request within 14 days, so this lead has expired. Their details were never shared.
    </div>
  );
}

// Full detail body for a visible-identity introducer lead (submitted / approved / rejected / converted).
function IntroLeadBody({ lead, onApprove, onReject, onConvert, onSaveNotes, onContactStatus, onClaim, currentUserId, unreadCount, onUnreadCleared }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [showMessages, setShowMessages] = useState(false);

  const isPending  = lead.status === "submitted";
  const isApproved = lead.status === "approved";
  const isRejected = lead.status === "rejected";
  const isConverted = lead.status === "converted";

  const handleReject = async () => { if (!reason.trim()) return; await onReject(lead.id, reason); };
  const handleClaim = async () => { setClaiming(true); await onClaim(lead.id); setClaiming(false); };

  return (
    <div>
      <div style={{ display: "flex", gap: 16, fontSize: 12.5, color: "#64748b", flexWrap: "wrap", marginBottom: 14 }}>
        {lead.client_email && <span>✉ {lead.client_email}</span>}
        {lead.client_phone && <span>📱 {lead.client_phone}</span>}
        <span style={{ color: "#94a3b8" }}>via {lead.introducer_name}</span>
        {isPending && <span>{ageLabel(lead.created_at)}</span>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12, marginBottom: lead.notes ? 12 : 0 }}>
        <MetaLabel label="Submitted" value={fmt(lead.created_at)} />
        {lead.reviewed_at && <MetaLabel label="Reviewed" value={fmt(lead.reviewed_at)} />}
        {lead.mortgage_type && <MetaLabel label="Type" value={lead.mortgage_type} />}
        {lead.loan_amount && <MetaLabel label="Loan amount" value={`£${parseFloat(lead.loan_amount).toLocaleString("en-GB")}`} />}
        {lead.employment_status && <MetaLabel label="Employment" value={lead.employment_status} />}
        {lead.credit_issues != null && (
          <MetaLabel label="Credit issues" value={lead.credit_issues ? "Yes" : "No"}
            valueStyle={{ color: lead.credit_issues ? "#dc2626" : "#16a34a", fontWeight: 600 }} />
        )}
      </div>

      {lead.notes && (
        <div style={{ background: "#f8fafc", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#475569", marginTop: 8, borderLeft: "3px solid #cbd5e1" }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8", display: "block", marginBottom: 3 }}>Introducer notes</span>
          {lead.notes}
        </div>
      )}

      {lead.rejection_reason && (
        <div style={{ background: "#fef2f2", borderLeft: "3px solid #fca5a5", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#475569", marginTop: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8", display: "block", marginBottom: 3 }}>Rejection reason</span>
          {lead.rejection_reason}
        </div>
      )}

      {isConverted && lead.deal_id && (
        <div style={{ marginTop: 12 }}>
          <a href={`/deal/${lead.deal_id}`} className="eq-link-btn">View Deal →</a>
        </div>
      )}

      {(isPending || isApproved) && <NotesBox lead={lead} onSaveNotes={onSaveNotes} />}
      {(isRejected || isConverted) && lead.broker_notes && (
        <div style={{ background: "#fffef5", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#475569", marginTop: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#94a3b8", display: "block", marginBottom: 3 }}>Your notes</span>
          {lead.broker_notes}
        </div>
      )}

      {isApproved && (
        <div style={{ padding: "12px 0 0", borderTop: "1px solid #f1f5f9", marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
          <RagStrip lead={lead} onContactStatus={onContactStatus} />
          <div>
            <button onClick={() => onConvert(lead)}
              className={lead.contact_status === "meeting_booked" ? "eq-btn-primary" : "eq-btn-secondary"}
              style={lead.contact_status === "meeting_booked" ? { background: "#10b981" } : { color: "#10b981", borderColor: "#6ee7b7" }}>
              → Convert to Deal
            </button>
          </div>
        </div>
      )}

      {isPending && (
        <div style={{ padding: "12px 0 0", borderTop: "1px solid #f1f5f9", marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <PendingContactStrip lead={lead} onContactStatus={onContactStatus} />
          {!lead.assigned_broker_id && (
            <button onClick={handleClaim} disabled={claiming} className="eq-btn-secondary"
              style={{ color: "#4338ca", borderColor: "#a5b4fc", background: "#eef2ff", alignSelf: "flex-start" }}>
              {claiming ? "Claiming…" : "🎯 Claim this lead"}
            </button>
          )}
          {!rejecting ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => onApprove(lead.id)} className="eq-btn-primary" style={{ background: "#10b981" }}>✓ Accept</button>
              <button onClick={() => setRejecting(true)} className="eq-btn-danger" style={{ background: "none", color: "#dc2626", border: "1.5px solid #fca5a5" }}>✕ Reject</button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <textarea placeholder="Reason for rejection (required)…" value={reason} onChange={e => setReason(e.target.value)} rows={2}
                style={{ width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", resize: "vertical", outline: "none" }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleReject} disabled={!reason.trim()} className="eq-btn-danger">Confirm Reject</button>
                <button onClick={() => { setRejecting(false); setReason(""); }} className="eq-btn-secondary">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {!isConverted && <AdditionalClientsSection parentType="introducer_lead" parentId={lead.id} />}

      <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 14 }}>
        <button onClick={() => setShowMessages(v => !v)}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
            background: showMessages ? "#0e1b2c" : "#f8fafc", border: "none", padding: "12px 4px",
            cursor: "pointer", color: showMessages ? "#10b981" : "#0e1b2c", fontWeight: 600, fontSize: 13.5,
          }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 16 }}>💬</span>
            <span>Messages with introducer</span>
            {unreadCount > 0 && <span style={{ background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10 }}>{unreadCount} new</span>}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, transform: showMessages ? "rotate(180deg)" : "none", display: "inline-block", color: showMessages ? "#10b981" : "#94a3b8" }}>▾</span>
        </button>
        {showMessages && (
          <div style={{ padding: "4px 4px 16px", background: "#0e1b2c", borderRadius: "0 0 8px 8px" }}>
            <BrokerMessageThread leadId={lead.id} onUnreadCleared={onUnreadCleared} />
          </div>
        )}
      </div>
    </div>
  );
}

// Additional clients (partner / co-applicant / friend, up to 4 more) attached
// to a lead before it's converted to a deal — carries over automatically at
// conversion (see dealController.js). Works for both lead types via
// GET/POST /api/deal-clients?enquiry_id= or ?introducer_lead_id= (5 Aug 2026).
function AdditionalClientsSection({ parentType, parentId }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", relationship: "Partner", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const paramKey = parentType === "introducer_lead" ? "introducer_lead_id" : "enquiry_id";

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/deal-clients?${paramKey}=${parentId}`, { headers: authHeaders() });
      const data = await res.json();
      setClients(Array.isArray(data.clients) ? data.clients : []);
    } catch { setClients([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [parentId]); // eslint-disable-line

  const add = async () => {
    if (!form.name.trim()) { setError("Name is required"); return; }
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/api/deal-clients`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ [paramKey]: parentId, ...form }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Failed to add client"); return; }
      setClients(c => [...c, data.client]);
      setForm({ name: "", relationship: "Partner", email: "", phone: "" });
      setShowAdd(false);
    } catch { setError("Something went wrong"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this client?")) return;
    try {
      const res = await fetch(`${API}/api/deal-clients/${id}`, { method: "DELETE", headers: authHeaders() });
      if (res.ok) setClients(cs => cs.filter(c => c.id !== id));
    } catch {}
  };

  if (loading) return null;

  return (
    <section className="eq-detail-section">
      <h4>
        Additional Clients ({clients.length}/4)
        {clients.length < 4 && (
          <button onClick={() => setShowAdd(s => !s)}
            style={{ marginLeft: 10, fontSize: 12, background: "none", border: "1px solid #1a56db", color: "#1a56db", borderRadius: 6, padding: "2px 8px", cursor: "pointer" }}>
            {showAdd ? "Cancel" : "+ Add"}
          </button>
        )}
      </h4>
      {showAdd && (
        <div style={{ marginBottom: 10, padding: 10, background: "#f8fafc", borderRadius: 8 }}>
          <input placeholder="Full name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            style={{ width: "100%", boxSizing: "border-box", padding: "6px 9px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, marginBottom: 6 }} />
          <select value={form.relationship} onChange={e => setForm(f => ({ ...f, relationship: e.target.value }))}
            style={{ width: "100%", boxSizing: "border-box", padding: "6px 9px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, marginBottom: 6 }}>
            <option value="Partner">Partner</option>
            <option value="Co-applicant">Co-applicant</option>
            <option value="Friend">Friend</option>
            <option value="Other">Other</option>
          </select>
          <input placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            style={{ width: "100%", boxSizing: "border-box", padding: "6px 9px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, marginBottom: 6 }} />
          <input placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            style={{ width: "100%", boxSizing: "border-box", padding: "6px 9px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, marginBottom: 6 }} />
          {error && <div style={{ color: "#dc2626", fontSize: 12, marginBottom: 6 }}>{error}</div>}
          <button onClick={add} disabled={busy}
            style={{ width: "100%", background: "#10b981", color: "#fff", border: "none", borderRadius: 6, padding: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            {busy ? "Adding…" : "Add Client"}
          </button>
        </div>
      )}
      {clients.length === 0 && !showAdd && (
        <p style={{ margin: 0, color: "#9ca3af", fontSize: 12.5, fontStyle: "italic" }}>
          Most leads are one client. Add another for a couple, co-applicants, or friends buying together.
        </p>
      )}
      {clients.map(c => (
        <div className="eq-detail-row" key={c.id}>
          <span>{c.relationship}</span>
          <span>
            {c.name}{c.email ? ` · ${c.email}` : ""}{c.phone ? ` · ${c.phone}` : ""}
            <button onClick={() => remove(c.id)} style={{ marginLeft: 8, background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 12 }}>Remove</button>
          </span>
        </div>
      ))}
    </section>
  );
}

function IntroducerDetailModal({ lead, onClose, currentUserId, unreadCount, handlers }) {
  const stageMeta = INTRO_STAGE[lead.status] || { bg: "#f3f4f6", colour: "#6b7280", label: lead.status };
  const redacted = PRE_CONSENT_STAGES.includes(lead.status);
  return (
    <div className="eq-modal-overlay" onClick={onClose}>
      <div className="eq-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="eq-modal-header">
          <div>
            <h2 style={{ marginBottom: 6 }}>{redacted ? "Referred lead" : lead.client_name}</h2>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <SourceTag source="introducer" />
              <StageBadge stageMeta={stageMeta} />
              <UrgencyBadgeC urgency={lead.urgency} />
              {!redacted && <AssignBadge item={{ assigned_broker_id: lead.assigned_broker_id, assigned_broker_name: lead.assigned_broker_name }} currentUserId={currentUserId} />}
            </div>
          </div>
          <button className="eq-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="eq-modal-body">
          {redacted
            ? <RedactedIntroCard lead={lead} />
            : <IntroLeadBody lead={lead} currentUserId={currentUserId} unreadCount={unreadCount} {...handlers} />}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Direct-lead detail pieces (ported from EnquiriesPage.jsx, unchanged logic)
// ─────────────────────────────────────────────────────────────────────────
function UrgencyPicker({ value, onChange }) {
  return (
    <div className="eq-urgency-picker">
      {Object.entries(URGENCY).map(([k, v]) => (
        <button key={k} type="button" className={`eq-urgency-option ${value === k ? "selected" : ""}`}
          style={value === k ? { borderColor: v.colour, background: v.bg } : {}} onClick={() => onChange(k)}>
          <span className="eq-urg-icon">{v.icon}</span>
          <span className="eq-urg-label" style={{ color: value === k ? v.colour : "#6b7280" }}>{v.label}</span>
        </button>
      ))}
    </div>
  );
}

function AddDirectLeadModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    client_name: "", client_email: "", client_phone: "", client_address: "",
    client_notes: "", source: "walk_in", product_type: "", urgency: "warm",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (!form.client_name.trim()) { setError("Client name is required"); return; }
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/api/enquiries`, { method: "POST", headers: authHeaders(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onCreate(data.enquiry);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="eq-modal-overlay" onClick={onClose}>
      <div className="eq-modal" onClick={e => e.stopPropagation()}>
        <div className="eq-modal-header"><h2>New Direct Lead</h2><button className="eq-modal-close" onClick={onClose}>✕</button></div>
        <form onSubmit={submit} className="eq-modal-body">
          {error && <div className="eq-error" style={{ marginBottom: 16 }}>{error}</div>}
          <div className="eq-form-grid">
            <div className="eq-form-group eq-span2"><label>Client Name *</label><input name="client_name" value={form.client_name} onChange={handle} placeholder="Full name" /></div>
            <div className="eq-form-group"><label>Email</label><input name="client_email" type="email" value={form.client_email} onChange={handle} placeholder="email@example.com" /></div>
            <div className="eq-form-group"><label>Phone</label><input name="client_phone" value={form.client_phone} onChange={handle} placeholder="+44..." /></div>
            <div className="eq-form-group eq-span2"><label>Address</label><input name="client_address" value={form.client_address} onChange={handle} placeholder="Property or home address" /></div>
            <div className="eq-form-group">
              <label>Source</label>
              <select name="source" value={form.source} onChange={handle}>
                {Object.entries(SOURCE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="eq-form-group">
              <label>Product Type</label>
              <select name="product_type" value={form.product_type} onChange={handle}>
                {PRODUCT_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
              </select>
            </div>
            <div className="eq-form-group eq-span2"><label>Urgency</label><UrgencyPicker value={form.urgency} onChange={v => setForm(f => ({ ...f, urgency: v }))} /></div>
            <div className="eq-form-group eq-span2"><label>Notes</label><textarea name="client_notes" value={form.client_notes} onChange={handle} rows={3} placeholder="Any additional details..." /></div>
          </div>
          <div className="eq-modal-footer">
            <button type="button" className="eq-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="eq-btn-primary" disabled={saving}>{saving ? "Saving…" : "Create Lead"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DirectEditModal({ enquiry, onClose, onSaved }) {
  const [form, setForm] = useState({
    client_name: enquiry.client_name || "", client_email: enquiry.client_email || "", client_phone: enquiry.client_phone || "",
    client_address: enquiry.client_address || "", client_notes: enquiry.client_notes || "",
    source: enquiry.source || "walk_in", product_type: enquiry.product_type || "", urgency: enquiry.urgency || "warm",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (!form.client_name.trim()) { setError("Client name is required"); return; }
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/api/enquiries/${enquiry.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save");
      onSaved(data.enquiry);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="eq-modal-overlay" onClick={onClose}>
      <div className="eq-modal" onClick={e => e.stopPropagation()}>
        <div className="eq-modal-header"><h2>Edit Lead</h2><button className="eq-modal-close" onClick={onClose}>✕</button></div>
        <form className="eq-modal-body" onSubmit={submit}>
          {error && <div className="eq-error" style={{ marginBottom: 16 }}>{error}</div>}
          <div className="eq-form-grid">
            <div className="eq-form-group eq-span2"><label>Client Name *</label><input name="client_name" value={form.client_name} onChange={handle} required /></div>
            <div className="eq-form-group"><label>Email</label><input name="client_email" type="email" value={form.client_email} onChange={handle} /></div>
            <div className="eq-form-group"><label>Phone</label><input name="client_phone" type="tel" value={form.client_phone} onChange={handle} /></div>
            <div className="eq-form-group eq-span2"><label>Address</label><input name="client_address" value={form.client_address} onChange={handle} /></div>
            <div className="eq-form-group">
              <label>Source</label>
              <select name="source" value={form.source} onChange={handle}>{Object.entries(SOURCE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </div>
            <div className="eq-form-group">
              <label>Product Type</label>
              <select name="product_type" value={form.product_type} onChange={handle}>{PRODUCT_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}</select>
            </div>
            <div className="eq-form-group eq-span2"><label>Urgency</label><UrgencyPicker value={form.urgency} onChange={v => setForm(f => ({ ...f, urgency: v }))} /></div>
            <div className="eq-form-group eq-span2"><label>Notes</label><textarea name="client_notes" rows={3} value={form.client_notes} onChange={handle} /></div>
          </div>
          <div className="eq-modal-footer">
            <button type="button" className="eq-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="eq-btn-primary" disabled={saving}>{saving ? "Saving…" : "Save Changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DirectAssignModal({ enquiry, advisers, onClose, onAssigned }) {
  const [adviserId, setAdviserId] = useState(enquiry.assigned_broker_id || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async e => {
    e.preventDefault();
    if (!adviserId) { setError("Please select an adviser"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/enquiries/${enquiry.id}/assign`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ assigned_broker_id: adviserId }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onAssigned(data.enquiry);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="eq-modal-overlay" onClick={onClose}>
      <div className="eq-modal eq-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="eq-modal-header"><h2>Assign Lead</h2><button className="eq-modal-close" onClick={onClose}>✕</button></div>
        <form onSubmit={submit} className="eq-modal-body">
          {error && <div className="eq-error" style={{ marginBottom: 12 }}>{error}</div>}
          <p style={{ margin: "0 0 16px", color: "#374151", fontSize: 14 }}>Assigning: <strong>{enquiry.client_name}</strong></p>
          <div className="eq-form-group">
            <label>Select Adviser</label>
            <select value={adviserId} onChange={e => setAdviserId(e.target.value)}>
              <option value="">— Choose adviser —</option>
              {advisers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="eq-modal-footer">
            <button type="button" className="eq-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="eq-btn-primary" disabled={saving}>{saving ? "Assigning…" : "Assign"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DirectLostModal({ enquiry, onClose, onMarked }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/enquiries/${enquiry.id}/status`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status: "lost", lost_reason: reason }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onMarked(data.enquiry);
      onClose();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <div className="eq-modal-overlay" onClick={onClose}>
      <div className="eq-modal eq-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="eq-modal-header"><h2>Mark as Lost</h2><button className="eq-modal-close" onClick={onClose}>✕</button></div>
        <form onSubmit={submit} className="eq-modal-body">
          <p style={{ margin: "0 0 16px", color: "#374151", fontSize: 14 }}>Mark <strong>{enquiry.client_name}</strong> as lost?</p>
          <div className="eq-form-group"><label>Reason (optional)</label><textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="e.g. Client went with another broker..." /></div>
          <div className="eq-modal-footer">
            <button type="button" className="eq-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="eq-btn-danger" disabled={saving}>{saving ? "Saving…" : "Mark Lost"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DirectDetailModal({ enquiry, canAssign, advisers, onClose, onUpdate, onConvert }) {
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showLost, setShowLost] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState("");

  const handleClaim = async () => {
    setClaiming(true); setClaimError("");
    try {
      const res = await fetch(`${API}/api/enquiries/${enquiry.id}/claim`, { method: "POST", headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to claim");
      onUpdate(data.enquiry);
    } catch (err) { setClaimError(err.message); }
    finally { setClaiming(false); }
  };

  // 25 July 2026: no longer calls the legacy one-shot PATCH /enquiries/:id/convert
  // (which invented product_type/fee_status/stage with no broker input, and had
  // its own separate stage='active' bug — see routes/enquiries.js). Now opens the
  // same prefilled /create-deal form introducer-lead conversion already used
  // (handleIntroConvert below), via POST /api/deals with enquiry_id.
  const handleConvert = () => {
    if (!window.confirm("Convert this lead to a Deal? You'll review and complete the details on the next screen.")) return;
    onConvert(enquiry);
  };

  const handleStatusChange = async newStatus => {
    setStatusUpdating(true);
    try {
      const res = await fetch(`${API}/api/enquiries/${enquiry.id}/status`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status: newStatus }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onUpdate(data.enquiry);
    } catch (err) { alert(err.message); }
    finally { setStatusUpdating(false); }
  };

  const isActive = !["converted", "lost"].includes(enquiry.status);
  const urg = URGENCY[enquiry.urgency] || URGENCY.warm;

  return (
    <div className="eq-modal-overlay" onClick={onClose}>
      <div className="eq-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
        <div className="eq-detail-header" style={{ borderTop: `4px solid ${urg.colour}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 17, color: "#0e1b2c", fontWeight: 700 }}>{enquiry.client_name}</h3>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <SourceTag source="direct" rawSource={enquiry.source} />
              <StageBadge stageMeta={DIRECT_STAGE[enquiry.status] || { bg: "#f3f4f6", colour: "#6b7280", label: enquiry.status }} />
              <UrgencyBadgeC urgency={enquiry.urgency} />
              <span className="eq-badge" style={{ background: "#f1f5f9", color: "#64748b" }}>{SOURCE_LABELS[enquiry.source] || enquiry.source}</span>
            </div>
          </div>
          <button className="eq-modal-close" onClick={onClose} style={{ marginLeft: 8, flexShrink: 0 }}>✕</button>
        </div>

        <div className="eq-detail-body">
          <section className="eq-detail-section">
            <h4>Contact Details</h4>
            {enquiry.client_email && <div className="eq-detail-row"><span>Email</span><a href={`mailto:${enquiry.client_email}`}>{enquiry.client_email}</a></div>}
            {enquiry.client_phone && <div className="eq-detail-row"><span>Phone</span><a href={`tel:${enquiry.client_phone}`}>{enquiry.client_phone}</a></div>}
            {enquiry.client_address && <div className="eq-detail-row"><span>Address</span><span>{enquiry.client_address}</span></div>}
          </section>

          {enquiry.status !== "converted" && <AdditionalClientsSection parentType="enquiry" parentId={enquiry.id} />}

          {enquiry.product_type && (
            <section className="eq-detail-section">
              <h4>Product</h4>
              <div className="eq-detail-row"><span>Type</span><span>{PRODUCT_TYPES.find(p => p.value === enquiry.product_type)?.label || enquiry.product_type}</span></div>
            </section>
          )}

          <section className="eq-detail-section">
            <h4>Assignment</h4>
            <div className="eq-detail-row"><span>Created by</span><span>{enquiry.created_by_name}</span></div>
            <div className="eq-detail-row"><span>Assigned to</span><span>{enquiry.assigned_broker_name || <em style={{ color: "#9ca3af" }}>Unassigned</em>}</span></div>
            <div className="eq-detail-row"><span>Created</span><span>{new Date(enquiry.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span></div>
          </section>

          {enquiry.funnel_answers && (
            <section className="eq-detail-section">
              <h4>Funnel Answers</h4>
              {(() => {
                let answers;
                try { answers = typeof enquiry.funnel_answers === "string" ? JSON.parse(enquiry.funnel_answers) : enquiry.funnel_answers; } catch { answers = null; }
                if (!answers || typeof answers !== "object") return null;
                const humanize = (k) => k.replace(/([A-Z])/g, " ").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()).trim();
                return Object.entries(answers).map(([k, v]) => (
                  <div className="eq-detail-row" key={k}><span>{humanize(k)}</span><span>{Array.isArray(v) ? (v.length ? v.join(", ") : "None") : String(v)}</span></div>
                ));
              })()}
            </section>
          )}

          {enquiry.client_notes && (
            <section className="eq-detail-section"><h4>Notes</h4><p style={{ margin: 0, color: "#374151", fontSize: 13.5, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{enquiry.client_notes}</p></section>
          )}

          {enquiry.status === "converted" && enquiry.deal_id && (
            <section className="eq-detail-section"><h4>Converted Deal</h4><a href={`/deal/${enquiry.deal_id}`} className="eq-link-btn">View Deal →</a></section>
          )}

          {enquiry.status === "lost" && enquiry.lost_reason && (
            <section className="eq-detail-section"><h4>Lost Reason</h4><p style={{ margin: 0, color: "#6b7280", fontSize: 13.5, fontStyle: "italic" }}>{enquiry.lost_reason}</p></section>
          )}
        </div>

        {isActive && !enquiry.assigned_broker_id && (
          <div style={{ padding: "0 20px 8px" }}>
            <button className="eq-btn-primary" onClick={handleClaim} disabled={claiming} style={{ width: "100%", background: "#6366f1" }}>{claiming ? "Claiming…" : "🎯 Claim this lead"}</button>
            {claimError && <p style={{ color: "#dc2626", fontSize: 12.5, margin: "6px 0 0" }}>{claimError}</p>}
          </div>
        )}

        {isActive && (
          <div className="eq-detail-actions">
            <button className="eq-btn-secondary" onClick={() => setShowEdit(true)}>✏️ Edit</button>
            {canAssign && <button className="eq-btn-secondary" onClick={() => setShowAssign(true)}>{enquiry.assigned_broker_name ? "Reassign" : "Assign"}</button>}
            {(enquiry.status === "assigned" || (enquiry.status === "new" && advisers.length === 0)) && (
              <button className="eq-btn-secondary" onClick={() => handleStatusChange("in_progress")} disabled={statusUpdating}>In Progress</button>
            )}
            <button className="eq-btn-primary" onClick={handleConvert} style={{ flex: 1 }}>→ Convert to Deal</button>
            <button className="eq-btn-danger" onClick={() => setShowLost(true)}>Lost</button>
          </div>
        )}

        {showEdit   && <DirectEditModal   enquiry={enquiry} onClose={() => setShowEdit(false)}   onSaved={updated => { onUpdate(updated); setShowEdit(false); }} />}
        {showAssign && <DirectAssignModal enquiry={enquiry} advisers={advisers} onClose={() => setShowAssign(false)} onAssigned={updated => { onUpdate(updated); setShowAssign(false); }} />}
        {showLost   && <DirectLostModal   enquiry={enquiry} onClose={() => setShowLost(false)}   onMarked={updated => { onUpdate(updated); setShowLost(false); }} />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Add Introducer Lead modal (manual entry) — unchanged from prior LeadsPage.jsx
// ─────────────────────────────────────────────────────────────────────────
function AddIntroducerLeadModal({ onClose, onCreated }) {
  const [introducers, setIntroducers] = useState([]);
  const [form, setForm] = useState({
    introducer_user_id: "", client_name: "", client_email: "", client_phone: "",
    mortgage_type: "", urgency: "warm", notes: "", employment_status: "", credit_issues: null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/api/introducers`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setIntroducers(d.introducers || d || [])).catch(() => {});
  }, []);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (!form.introducer_user_id) { setError("Please select an introducer"); return; }
    if (!form.client_name.trim()) { setError("Client name is required"); return; }
    if (!form.client_email.trim()) { setError("Client email is required"); return; }
    if (!form.client_phone.trim()) { setError("Client phone is required"); return; }
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/api/introducer-leads/broker-create`, { method: "POST", headers: authHeaders(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create lead");
      onCreated(data.lead);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="eq-modal-overlay" onClick={onClose}>
      <div className="eq-modal" onClick={e => e.stopPropagation()}>
        <div className="eq-modal-header"><h2>Add Introducer Lead</h2><button className="eq-modal-close" onClick={onClose}>✕</button></div>
        <form onSubmit={submit} className="eq-modal-body">
          {error && <div className="eq-error">{error}</div>}
          <div className="eq-form-grid">
            <div className="eq-form-group eq-span2">
              <label>Introducer *</label>
              <select name="introducer_user_id" value={form.introducer_user_id} onChange={handle} required>
                <option value="">— Select introducer —</option>
                {introducers.flatMap(i => (i.members || []).map(m => (
                  <option key={m.id} value={m.id}>{m.name} — {i.name}</option>
                )))}
              </select>
            </div>
            <div className="eq-form-group eq-span2"><label>Client Name *</label><input name="client_name" value={form.client_name} onChange={handle} placeholder="Full name" /></div>
            <div className="eq-form-group"><label>Email *</label><input name="client_email" type="email" value={form.client_email} onChange={handle} placeholder="email@example.com" /></div>
            <div className="eq-form-group"><label>Phone *</label><input name="client_phone" value={form.client_phone} onChange={handle} placeholder="+44..." /></div>
            <div className="eq-form-group">
              <label>Mortgage Type</label>
              <select name="mortgage_type" value={form.mortgage_type} onChange={handle}>{MORTGAGE_TYPES.map(mt => <option key={mt.value} value={mt.value}>{mt.label}</option>)}</select>
            </div>
            <div className="eq-form-group">
              <label>Employment Status</label>
              <select name="employment_status" value={form.employment_status} onChange={handle}>
                <option value="">— Select —</option>
                <option value="Employed">Employed</option>
                <option value="Self-employed">Self-employed</option>
                <option value="Retired">Retired</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="eq-form-group eq-span2">
              <label>Urgency</label>
              <div className="eq-urgency-picker">
                {Object.entries(URGENCY).map(([k, v]) => (
                  <label key={k} className={`eq-urgency-option ${form.urgency === k ? "selected" : ""}`}
                    style={form.urgency === k ? { borderColor: v.colour, background: v.bg } : {}}>
                    <input type="radio" name="urgency" value={k} checked={form.urgency === k} onChange={handle} style={{ display: "none" }} />
                    <span style={{ color: v.colour, fontWeight: 700 }}>{v.icon} {v.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="eq-form-group" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <label style={{ margin: 0 }}>Credit issues?</label>
              <select name="credit_issues" value={form.credit_issues === null ? "" : String(form.credit_issues)} onChange={e => setForm(f => ({ ...f, credit_issues: e.target.value === "" ? null : e.target.value === "true" }))}>
                <option value="">Unknown</option>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div className="eq-form-group eq-span2"><label>Notes</label><textarea name="notes" value={form.notes} onChange={handle} rows={3} placeholder="Any details from the introducer..." /></div>
          </div>
          <div className="eq-modal-footer">
            <button type="button" className="eq-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="eq-btn-primary" disabled={saving}>{saving ? "Saving…" : "Add Lead"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Main merged LeadsPage
// ─────────────────────────────────────────────────────────────────────────
const STAGE_FILTER_GROUPS = [
  { group: "Introducer", options: [
    { value: "submitted", label: "Pending review" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "awaiting_consent", label: "Awaiting verification" },
    { value: "consent_declined", label: "Client declined" },
    { value: "consent_expired", label: "Verification expired" },
    { value: "converted", label: "Converted" },
  ] },
  { group: "Direct", options: [
    { value: "new", label: "New" },
    { value: "assigned", label: "Assigned" },
    { value: "in_progress", label: "In Progress" },
    { value: "converted", label: "Converted" },
    { value: "lost", label: "Lost" },
  ] },
];

export default function LeadsPage() {
  const navigate = useNavigate();
  const mobile = useMobile();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const currentUserId = user.id;
  const isIntroducer = user.role === "introducer";
  const userLevel = user.brokerLevel || user.broker_level;
  const canAssign = user.role === "owner" || userLevel === "owner" || userLevel === "manager";

  const [introLeads, setIntroLeads] = useState([]);
  const [directLeads, setDirectLeads] = useState([]);
  const [advisers, setAdvisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("leadsViewMode") === "board" ? "board" : "list"; }
    catch { return "list"; }
  });
  useEffect(() => {
    try { localStorage.setItem("leadsViewMode", viewMode); } catch { /* noop */ }
  }, [viewMode]);

  const [filters, setFilters] = useState({ source: "", stage: "", urgency: "", assigned: "", search: "" });
  const [sort, setSort] = useState("newest");
  // Archived view: a lead archived from the mobile app (migration 019,
  // 25 July 2026) was previously only visible/restorable via the mobile
  // "Archived" tab — web had no equivalent, so it just vanished here. This
  // mirrors mobile’s pattern: a separate server-side-filtered view, not a
  // client-side filter over the same fetched list (archived rows are excluded
  // from the normal fetch entirely).
  const [archivedView, setArchivedView] = useState(false);

  const [selected, setSelected] = useState(null); // { source: 'introducer'|'direct', id }
  const [showAddIntro, setShowAddIntro] = useState(false);
  const [showAddDirect, setShowAddDirect] = useState(false);
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const loadIntro = useCallback(async () => {
    if (isIntroducer) return;
    try {
      const res = await api.get(`/introducer-leads/broker${archivedView ? "?archived=1" : ""}`);
      setIntroLeads(res.data.leads || []);
    } catch {}
  }, [isIntroducer, archivedView]);

  const loadDirect = useCallback(async () => {
    if (isIntroducer) return;
    try {
      const res = await fetch(`${API}/api/enquiries${archivedView ? "?archived=1" : ""}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setDirectLeads(data.enquiries || []);
    } catch {}
  }, [isIntroducer, archivedView]);

  const loadAll = useCallback(async () => { setLoading(true); await Promise.all([loadIntro(), loadDirect()]); setLoading(false); }, [loadIntro, loadDirect]);

  // Re-fetch whenever the archived/live view toggles, not just on mount.
  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    fetch(`${API}/api/broker/team`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setAdvisers(Array.isArray(d) ? d : (d.users || d.team || d.members || []))).catch(() => {});
  }, []);

  const items = useMemo(() => [
    ...introLeads.map(normalizeIntroducer),
    ...directLeads.map(normalizeDirect),
  ], [introLeads, directLeads]);

  // Deep link: "?open=<id>" (from lead-notification / claim emails) opens that
  // lead's detail modal directly. leadDistribution.js links introducer leads
  // to /leads?open=<id> and direct leads to /enquiries?open=<id> — both routes
  // render this same page, so the path tells us which source table the id
  // belongs to.
  useEffect(() => {
    if (deepLinkHandled || loading || items.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const openId = params.get("open");
    if (openId) {
      const preferredSource = window.location.pathname === "/enquiries" ? "direct" : "introducer";
      let match = items.find(it => it.source === preferredSource && String(it.id) === openId);
      if (!match) match = items.find(it => String(it.id) === openId);
      if (match) setSelected({ source: match.source, id: match.id });
      params.delete("open");
      const rest = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
    }
    setDeepLinkHandled(true);
  }, [items, loading, deepLinkHandled]);

  const filtered = useMemo(() => {
    let list = items;
    if (filters.source === "facebook") list = list.filter(it => it.raw?.source && it.raw.source.startsWith("fb_"));
    else if (filters.source) list = list.filter(it => it.source === filters.source);
    if (filters.stage) list = list.filter(it => it.stage === filters.stage);
    if (filters.urgency) list = list.filter(it => it.urgency === filters.urgency);
    if (filters.assigned === "unassigned") list = list.filter(it => !it.assigned_broker_id);
    if (filters.search.trim()) {
      const q = filters.search.trim().toLowerCase();
      list = list.filter(it =>
        (it.client_name || "").toLowerCase().includes(q) ||
        (it.client_email || "").toLowerCase().includes(q) ||
        (it.client_phone || "").toLowerCase().includes(q) ||
        (it.introducer_name || "").toLowerCase().includes(q)
      );
    }
    const sorted = [...list];
    const urgRank = { hot: 0, warm: 1, cold: 2 };
    if (sort === "newest") sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    else if (sort === "oldest") sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    else if (sort === "urgency") sorted.sort((a, b) => (urgRank[a.urgency] ?? 3) - (urgRank[b.urgency] ?? 3));
    else if (sort === "name") sorted.sort((a, b) => (a.client_name || "").localeCompare(b.client_name || ""));
    return sorted;
  }, [items, filters, sort]);

  const unassignedCount = items.filter(it => !it.assigned_broker_id && !["converted", "lost", "rejected", "consent_declined", "consent_expired"].includes(it.stage)).length;

  // ── Introducer-lead handlers ──
  const handleApprove = async (id) => {
    try { await api.patch(`/introducer-leads/${id}/status`, { status: "approved" }); showToast("Lead approved — you can now convert it to a deal."); await loadIntro(); }
    catch (err) { showToast(err.response?.data?.error || "Failed to approve.", "error"); }
  };
  const handleReject = async (id, reason) => {
    try { await api.patch(`/introducer-leads/${id}/status`, { status: "rejected", rejection_reason: reason }); showToast("Lead rejected."); await loadIntro(); }
    catch (err) { showToast(err.response?.data?.error || "Failed to reject.", "error"); }
  };
  const handleIntroClaim = async (id) => {
    try { await api.post(`/introducer-leads/${id}/claim`); showToast("Lead claimed — it's yours now."); await loadIntro(); }
    catch (err) { showToast(err.response?.data?.error || "Failed to claim — someone may have just beaten you to it.", "error"); }
  };
  const handleSaveNotes = async (id, broker_notes) => {
    try { await api.patch(`/introducer-leads/${id}/broker-notes`, { broker_notes }); await loadIntro(); }
    catch (err) { showToast(err.response?.data?.error || "Failed to save note.", "error"); }
  };
  const handleContactStatus = async (id, contact_status) => {
    try {
      const res = await api.patch(`/introducer-leads/${id}/contact-status`, { contact_status });
      if (res.data.status === "rejected") showToast("Lead closed — introducer notified by email.");
      await loadIntro();
    } catch (err) { showToast(err.response?.data?.error || "Failed to update.", "error"); }
  };
  const handleIntroConvert = (lead) => {
    const params = new URLSearchParams({
      lead_id: lead.id, introducer_id: lead.introducer_user_id,
      client_name: lead.client_name || "", client_email: lead.client_email || "",
      client_phone: lead.client_phone || "", client_notes: lead.notes || "",
    });
    navigate(`/create-deal?${params.toString()}`);
  };

  // ── Direct-lead callbacks ──
  const handleDirectCreate = (enq) => { setDirectLeads(prev => [enq, ...prev]); };
  const handleDirectUpdate = (updated) => {
    setDirectLeads(prev => prev.map(e => e.id === updated.id ? updated : e));
  };
  const handleDirectConvertToDeal = (enquiry) => {
    const params = new URLSearchParams({
      enquiry_id: enquiry.id,
      client_name: enquiry.client_name || "",
      client_email: enquiry.client_email || "",
      client_phone: enquiry.client_phone || "",
      client_notes: enquiry.client_notes || enquiry.notes || "",
      product_type: enquiry.product_type || "",
    });
    navigate(`/create-deal?${params.toString()}`);
  };

  const handleAddIntroCreated = () => { setShowAddIntro(false); loadIntro(); };

  // ── Archive / restore (25 July 2026) ──
  // Silent + reversible, notifies nobody — same distinction the mobile side
  // already draws (see migrations/019_lead_archiving.sql): this is NOT the
  // same as rejecting an introducer lead or marking a direct lead lost,
  // both of which fire real outbound emails/pushes.
  const handleArchiveIntro = async (id) => {
    try { await api.post(`/introducer-leads/${id}/archive`); showToast("Lead archived."); await loadIntro(); }
    catch (err) { showToast(err.response?.data?.error || "Failed to archive.", "error"); }
  };
  const handleRestoreIntro = async (id) => {
    try { await api.post(`/introducer-leads/${id}/unarchive`); showToast("Lead restored."); await loadIntro(); }
    catch (err) { showToast(err.response?.data?.error || "Failed to restore.", "error"); }
  };
  const handleArchiveDirect = async (id) => {
    try {
      const res = await fetch(`${API}/api/enquiries/${id}/archive`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to archive");
      showToast("Lead archived."); await loadDirect();
    } catch (err) { showToast(err.message || "Failed to archive.", "error"); }
  };
  const handleRestoreDirect = async (id) => {
    try {
      const res = await fetch(`${API}/api/enquiries/${id}/unarchive`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to restore");
      showToast("Lead restored."); await loadDirect();
    } catch (err) { showToast(err.message || "Failed to restore.", "error"); }
  };
  const toggleArchivedView = () => {
    // Clears the OTHER filters (mirrors the Unassigned pill's exclusivity —
    // archived-ness is orthogonal to source/stage/urgency, so combining them
    // is more likely to confuse than help) without touching archivedView
    // itself, which this toggles directly.
    setFilters({ source: "", stage: "", urgency: "", assigned: "", search: "" });
    setArchivedView(v => !v);
  };

  const clearFilters = () => { setFilters({ source: "", stage: "", urgency: "", assigned: "", search: "" }); setArchivedView(false); };
  const hasActiveFilters = filters.source || filters.stage || filters.urgency || filters.assigned || filters.search || archivedView;

  if (isIntroducer) {
    return <div className="broker-dashboard"><Sidebar /><main className="main-content"><div className="empty-state"><p>Access denied.</p></div></main></div>;
  }

  const selectedIntro = selected?.source === "introducer" ? introLeads.find(l => l.id === selected.id) : null;
  const selectedDirect = selected?.source === "direct" ? directLeads.find(e => e.id === selected.id) : null;

  return (
    <div className="broker-dashboard">
      <Sidebar />
      {/* .main-content (not .eq-main) — the proven sidebar-offset pattern every other
          page uses (flex:1 + margin-left:70px + explicit width:calc(100% - 70px));
          .eq-main only had the first two, which let it overflow under the fixed
          sidebar and made the hover-expand appear to cover the page. App.css's
          global mobile query also only resets margin/width for .main-content. */}
      <main className="main-content" style={{ maxWidth: 1400, overflowX: "hidden", ...(mobile ? {} : { padding: 32 }) }}>
        {toast && (
          <div style={{ position: "fixed", bottom: 24, right: 24, padding: "12px 20px", borderRadius: 10, fontSize: 14, fontWeight: 500, zIndex: 1100, boxShadow: "0 4px 20px rgba(0,0,0,0.2)", background: toast.type === "error" ? "#dc2626" : "#0e1b2c", color: "#fff" }}>
            {toast.msg}
          </div>
        )}

        <div className="eq-page-header">
          <div>
            <h1>Leads</h1>
            <p>Everything referred by your introducer network plus direct enquiries, in one place</p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
            <button className="eq-btn-secondary" onClick={() => downloadCsv(`${API}/api/exports/leads`, "introducer-leads.csv")} title="Export introducer leads as CSV">⬇ Introducer CSV</button>
            <button className="eq-btn-secondary" onClick={() => downloadCsv(`${API}/api/exports/enquiries`, "direct-leads.csv")} title="Export direct leads as CSV">⬇ Direct CSV</button>
            <button className="eq-btn-primary" onClick={() => setShowAddIntro(true)}>+ Add Introducer Lead</button>
            <button className="eq-btn-primary" onClick={() => setShowAddDirect(true)} style={{ background: "#1d4ed8" }}>+ Add Direct Lead</button>
          </div>
        </div>

        {/* Filter + sort bar */}
        <div className="eq-urgency-bar">
          <button className={`eq-urgency-chip ${filters.source === "" ? "active" : ""}`} onClick={() => setFilters(f => ({ ...f, source: "" }))}>All</button>
          <button className={`eq-urgency-chip ${filters.source === "introducer" ? "active" : ""}`} onClick={() => setFilters(f => ({ ...f, source: f.source === "introducer" ? "" : "introducer" }))}>🤝 Introducer</button>
          <button className={`eq-urgency-chip ${filters.source === "direct" ? "active" : ""}`} onClick={() => setFilters(f => ({ ...f, source: f.source === "direct" ? "" : "direct" }))}>📋 Direct</button>
          <button className={`eq-urgency-chip ${filters.source === "facebook" ? "active" : ""}`} onClick={() => setFilters(f => ({ ...f, source: f.source === "facebook" ? "" : "facebook" }))}>📘 Facebook</button>

          <div className="eq-urgency-bar-sep" />

          {/* Archived (25 July 2026): a lead archived from the mobile app previously
              had no restore path on web at all — this mirrors mobile's separate
              server-side-filtered "Archived" view rather than a client-side filter. */}
          <button className={`eq-urgency-chip ${archivedView ? "active" : ""}`}
            style={archivedView ? { background: "#f1f5f9", borderColor: "#64748b", color: "#334155" } : {}}
            title="Leads archived from the mobile app (or here) — hidden from the normal list, never deleted"
            onClick={toggleArchivedView}>
            🗄 Archived
          </button>

          <div className="eq-urgency-bar-sep" />

          {Object.entries(URGENCY).map(([k, v]) => (
            <button key={k} className={`eq-urgency-chip chip-${k} ${filters.urgency === k ? "active" : ""}`} onClick={() => setFilters(f => ({ ...f, urgency: f.urgency === k ? "" : k }))}>
              {v.icon} {v.label}
            </button>
          ))}

          <div className="eq-urgency-bar-sep" />

          {canAssign && (
            <button className={`eq-urgency-chip ${filters.assigned === "unassigned" ? "active" : ""}`}
              style={filters.assigned === "unassigned" ? { background: "#fff1f1", borderColor: "#dc2626", color: "#dc2626" } : {}}
              title="Shows unassigned leads only — clears other filters, since assignment is independent of urgency/stage/source"
              onClick={() => setFilters(f => f.assigned === "unassigned"
                ? { source: "", stage: "", urgency: "", assigned: "", search: "" }
                : { source: "", stage: "", urgency: "", assigned: "unassigned", search: "" })}>
              ⚠ Unassigned
              {unassignedCount > 0 && <span className="eq-chip-count" style={{ background: "#dc2626" }}>{unassignedCount}</span>}
            </button>
          )}

          <select className="eq-select" style={{ width: "auto", padding: "5px 10px", fontSize: 13, marginLeft: 4 }}
            value={filters.stage} onChange={e => setFilters(f => ({ ...f, stage: e.target.value }))}>
            <option value="">All stages</option>
            {STAGE_FILTER_GROUPS.map(g => (
              <optgroup key={g.group} label={g.group}>
                {g.options.map(o => <option key={`${g.group}-${o.value}`} value={o.value}>{o.label}</option>)}
              </optgroup>
            ))}
          </select>

          <select className="eq-select" style={{ width: "auto", padding: "5px 10px", fontSize: 13 }} value={sort} onChange={e => setSort(e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="urgency">Urgency (Hot → Cold)</option>
            <option value="name">Name A–Z</option>
          </select>

          <input className="eq-input" style={{ width: 220, padding: "5px 10px", fontSize: 13, marginLeft: "auto" }}
            placeholder="Search name, email, phone, introducer…" value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />

          {hasActiveFilters && <button className="eq-clear-filters" onClick={clearFilters}>Clear ✕</button>}
        </div>

        <div className="eq-content">
          <div className="eq-list">
            {loading ? (
              <div className="eq-loading">Loading leads…</div>
            ) : filtered.length === 0 ? (
              <div className="eq-empty">
                <div className="eq-empty-icon">{archivedView ? "🗄" : "📋"}</div>
                <h3>{archivedView ? "Nothing archived" : "No leads found"}</h3>
                <p>{archivedView
                  ? "Leads archived here or from the mobile app are hidden from the normal list but never deleted."
                  : (hasActiveFilters ? "Try clearing your filters." : "Invite an introducer or log a lead when a client walks in, calls or enquires online.")}</p>
              </div>
            ) : viewMode === "board" ? (
              <LeadsKanbanBoard items={filtered} onSelect={setSelected} currentUserId={currentUserId} />
            ) : (
              filtered.map(it => (
                <div key={it.uid} className={`eq-card ${["converted", "lost", "rejected", "consent_declined", "consent_expired"].includes(it.stage) ? "eq-card-closed" : ""}`}
                  onClick={() => setSelected({ source: it.source, id: it.id })}>
                  <div className={`eq-card-left ${it.urgency || "warm"}`} />
                  <div className="eq-card-body">
                    <div className="eq-card-row1">
                      <span className="eq-card-name">{it.client_name || "Client details hidden"}</span>
                      <div className="eq-card-badges">
                        <SourceTag source={it.source} rawSource={it.raw?.source} />
                        <StageBadge stageMeta={it.stageMeta} />
                        <UrgencyBadgeC urgency={it.urgency} />
                        {it.unread_count > 0 && <span className="eq-badge" style={{ background: "#ef4444", color: "#fff" }}>💬 {it.unread_count} new</span>}
                      </div>
                    </div>
                    <div className="eq-card-row2">
                      {it.client_phone && <span>📱 {it.client_phone}</span>}
                      {it.client_email && <span>✉ {it.client_email}</span>}
                      {it.source === "introducer" ? <span>via {it.introducer_name}</span> : <span>{it.sourceLabel}</span>}
                    </div>
                    <div className="eq-card-row3">
                      <AssignBadge item={it} currentUserId={currentUserId} />
                      <span>{new Date(it.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                      <button
                        className="eq-btn-secondary"
                        style={{ marginLeft: "auto", padding: "3px 10px", fontSize: 12 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const archiveFn = it.source === "introducer" ? handleArchiveIntro : handleArchiveDirect;
                          const restoreFn = it.source === "introducer" ? handleRestoreIntro : handleRestoreDirect;
                          (archivedView ? restoreFn : archiveFn)(it.id);
                        }}
                      >
                        {archivedView ? "↩ Restore" : "🗄 Archive"}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {selectedIntro && (
        <IntroducerDetailModal
          lead={selectedIntro}
          onClose={() => setSelected(null)}
          currentUserId={currentUserId}
          unreadCount={selectedIntro.unread_count || 0}
          handlers={{
            onApprove: handleApprove, onReject: handleReject, onConvert: handleIntroConvert,
            onSaveNotes: handleSaveNotes, onContactStatus: handleContactStatus, onClaim: handleIntroClaim,
            onUnreadCleared: () => {},
          }}
        />
      )}

      {selectedDirect && (
        <DirectDetailModal
          enquiry={selectedDirect}
          canAssign={canAssign}
          advisers={advisers}
          onClose={() => setSelected(null)}
          onUpdate={handleDirectUpdate}
          onConvert={handleDirectConvertToDeal}
        />
      )}

      {showAddIntro && <AddIntroducerLeadModal onClose={() => setShowAddIntro(false)} onCreated={handleAddIntroCreated} />}
      {showAddDirect && <AddDirectLeadModal onClose={() => setShowAddDirect(false)} onCreate={enq => { handleDirectCreate(enq); setShowAddDirect(false); }} />}
    </div>
  );
}
